import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight, Code, Smartphone, Palette, TrendingUp, ShoppingCart, Brain, BarChart, Cloud, Link as LinkIcon, PenTool } from 'lucide-react';
import { Link } from 'react-router-dom';

const ServicesGrid = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const services = [
    {
      id: 1,
      title: 'Web Development',
      icon: Code,
      description: 'Custom web applications with cutting-edge tech',
      color: '#d987ff',
      path: '/services/web-development'
    },
    {
      id: 2,
      title: 'Mobile App',
      icon: Smartphone,
      description: 'Native & cross-platform mobile solutions',
      color: '#ffd1e7',
      path: '/services/mobile-app'
    },
    {
      id: 3,
      title: 'UI/UX Design',
      icon: Palette,
      description: 'User-centric designs that convert',
      color: '#88a2ff',
      path: '/services/ui-ux-design'
    },
    {
      id: 4,
      title: 'Digital Marketing',
      icon: TrendingUp,
      description: 'Data-driven marketing strategies',
      color: '#b7fbff',
      path: '/services/digital-marketing'
    },
    {
      id: 5,
      title: 'E-Commerce',
      icon: ShoppingCart,
      description: 'Scalable online stores that sell',
      color: '#ffd1e7',
      path: '/services/e-commerce'
    },
    {
      id: 6,
      title: 'AI & ML',
      icon: Brain,
      description: 'Intelligent automation solutions',
      color: '#d987ff',
      path: '/services/ai-ml'
    },
    {
      id: 7,
      title: 'Data Analytics',
      icon: BarChart,
      description: 'Transform data into insights',
      color: '#88a2ff',
      path: '/services/data-analytics'
    },
    {
      id: 8,
      title: 'Cloud Solutions',
      icon: Cloud,
      description: 'Scalable cloud infrastructure',
      color: '#b7fbff',
      path: '/services/cloud-solutions'
    },
    {
      id: 9,
      title: 'Blockchain',
      icon: LinkIcon,
      description: 'Secure Web3 & DeFi solutions',
      color: '#d987ff',
      path: '/services/blockchain'
    },
    {
      id: 10,
      title: 'Copywriting',
      icon: PenTool,
      description: 'Compelling content that converts',
      color: '#ffd1e7',
      path: '/services/creative-copywriting'
    }
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % services.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + services.length) % services.length);
  };

  const getVisibleServices = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + services.length) % services.length;
      visible.push({ ...services[index], position: i });
    }
    return visible;
  };

  return (
    <section className="relative h-screen py-20 px-4 lg:px-8 bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a] text-white overflow-hidden flex items-center">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0]
          }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute top-20 left-0 w-96 h-96 bg-mid-purple opacity-10 blur-3xl rounded-full" 
        />
        <motion.div 
          animate={{ 
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90]
          }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute bottom-20 right-0 w-96 h-96 bg-light-pink opacity-10 blur-3xl rounded-full" 
        />
      </div>

      <div className="max-w-8xl mx-auto relative z-10 w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-block mb-4"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          >
            <span className="text-light-pink text-6xl">✦</span>
          </motion.div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
            Our
            <br />
            <span className="bg-gradient-to-r from-light-pink via-mid-purple to-light-blue bg-clip-text text-transparent">
              Services
            </span>
          </h2>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
            Explore our comprehensive range of digital solutions
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div className="relative h-[400px] flex items-center justify-center">
          {/* Cards */}
          <AnimatePresence mode="popLayout">
            {getVisibleServices().map((service) => (
              <motion.div
                key={service.id}
                initial={{ 
                  opacity: 0,
                  scale: 0.8,
                  x: service.position * 400
                }}
                animate={{ 
                  opacity: service.position === 0 ? 1 : 0.4,
                  scale: service.position === 0 ? 1 : 0.75,
                  x: service.position * 420,
                  y: Math.abs(service.position) * 20,
                  zIndex: service.position === 0 ? 10 : 5
                }}
                exit={{ 
                  opacity: 0,
                  scale: 0.6
                }}
                transition={{ 
                  duration: 0.5,
                  type: "spring",
                  stiffness: 100
                }}
                className="absolute"
                style={{ 
                  pointerEvents: service.position === 0 ? 'auto' : 'none'
                }}
              >
                <Link to={service.path}>
                  <motion.div
                    whileHover={{ scale: service.position === 0 ? 1.05 : 1 }}
                    className={`w-80 h-96 rounded-3xl p-8 backdrop-blur-xl border-2 flex flex-col justify-between transition-all ${
                      service.position === 0 
                        ? 'bg-white/10 border-white/30 shadow-2xl' 
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <motion.div
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                        className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
                        style={{ backgroundColor: `${service.color}30` }}
                      >
                        <service.icon size={40} style={{ color: service.color }} />
                      </motion.div>
                    </div>

                    {/* Content */}
                    <div className="flex-grow">
                      <h3 className="text-3xl font-bold mb-4 text-white">
                        {service.title}
                      </h3>
                      <p className="text-lg text-white/70 leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    {/* CTA */}
                    {service.position === 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center gap-2 text-light-pink font-semibold group"
                      >
                        <span>Learn More</span>
                        <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
                      </motion.div>
                    )}
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 z-20 w-14 h-14 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all hover:scale-110"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 z-20 w-14 h-14 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all hover:scale-110"
          >
            <ChevronRight size={28} />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-12">
          {services.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-light-pink w-8' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* View All Services Link */}
        <div className="text-center mt-12">
          <Link to="/services">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 rounded-xl bg-gradient-to-r from-light-pink to-mid-purple text-white font-bold text-sm uppercase tracking-wider shadow-2xl hover:shadow-light-pink/50 transition-all inline-flex items-center gap-3 group"
            >
              View All Services
              <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
            </motion.button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
